import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data.templates)).catch(() => toast.error("Failed to load templates"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/templates/${editing.key}`, { body });
      toast.success("Template updated — used by all future reminders");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-templates-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Message Templates</h1>
        <p className="text-sm text-muted-foreground">WhatsApp & email templates used by reminders and automation. Placeholders: {"{po_number}"}, {"{company}"}, {"{expected_date}"}, {"{requested_date}"}, {"{current_date}"}, {"{reason}"}.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="templates-grid">
        {templates.filter((t) => t.key !== "generic").map((t) => (
          <div key={t.key} data-testid={`template-${t.key}`} className="rounded-lg border bg-card p-4 transition-all duration-150 hover:-translate-y-[1px] hover:shadow-sm">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-3.5 w-3.5 text-slate-400" />{t.name}</p>
              <div className="flex items-center gap-2">
                {t.customized && <span className="rounded-full bg-[#DBEAFE] px-2 py-0.5 text-[10px] font-semibold text-[#1E40AF]">CUSTOMIZED</span>}
                <Button size="icon" variant="ghost" data-testid={`template-edit-${t.key}`} onClick={() => { setEditing(t); setBody(t.body); }}><Pencil className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="mt-2 whitespace-pre-wrap rounded-md bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-600">{t.body}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Template — {editing?.name}</DialogTitle>
            <DialogDescription>Changes apply to all future reminders and automated messages.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Template body</Label><Textarea data-testid="template-body-input" rows={5} className="mt-1.5 font-mono text-xs" value={body} onChange={(e) => setBody(e.target.value)} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" data-testid="template-save-btn" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Template
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
